import * as React from 'react'
import { Booking, BookingColumnConfig, BookingColumnProperty, BookingColumns, BookingProperty, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import moment from '../../utilities/Moment'
import styled from 'styled-components'
import Avatar from '../Avatar/Avatar'
import { locationLabel } from '../Scheduling/Booker/format'

const Primary = styled.div`
  font-weight: 600;
`

const Secondary = styled.div`
  color: #637381;
  font-size: 13px;
`

const HostContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export const DEFAULT_BOOKING_COLUMNS: BookingColumnProperty[] = [
  'start_time',
  'booker_name',
  'event_type',
  'host',
  'location',
]

const BookingColumnConfigs: Record<BookingColumnProperty, BookingColumnConfig> = {
  start_time: {
    header: () => ({ title: i18n.t('Booking::start_time') }),
    render: ({ booking }) => {
      const start = moment(booking.start_time)
      const end = moment(booking.end_time)

      return (
        <>
          <Primary>{start.format('ddd D MMM YYYY')}</Primary>
          <Secondary>{start.format('HH:mm')} - {end.format('HH:mm')}</Secondary>
        </>
      )
    },
  },
  booker_name: {
    header: () => ({ title: i18n.t('Booking::booker_name') }),
    render: ({ booking }) => (
      <>
        <Primary>{booking.booker_name}</Primary>
        <Secondary>{booking.booker_email}</Secondary>
      </>
    ),
  },
  event_type: {
    header: () => ({ title: i18n.t('Booking::event_type') }),
    render: ({ booking }) => (
      <>
        <Primary>{booking.event_type.title}</Primary>
        <Secondary>{i18n.t('Bookings::{{count}}m', { count: booking.event_type.duration })}</Secondary>
      </>
    ),
  },
  host: {
    header: () => ({ title: i18n.t('Booking::host') }),
    render: ({ booking }) => (
      <HostContainer>
        <Avatar name={booking.host.name} width={24} rounded />
        {booking.host.name}
      </HostContainer>
    ),
  },
  location: {
    header: () => ({ title: i18n.t('Booking::location') }),
    render: ({ booking }) => [locationLabel(i18n.t, booking.location), booking.location_value].filter(Boolean).join(' · ') || '-',
  },
  guests: {
    header: () => ({ title: i18n.t('Booking::guests') }),
    render: ({ booking }) => booking.guests.length > 0 ? booking.guests.join(', ') : '-',
  },
  created_at: {
    header: () => ({ title: i18n.t('Booking::created_at') }),
    render: ({ booking, setting }) => booking.created_at ? moment(booking.created_at).format(setting.date_format) : '-',
  },
}

const ELLIPSED_COLUMNS: BookingColumnProperty[] = ['booker_name', 'event_type', 'guests']

export function getBookingTableRenderer(options: ResourceRendererOptions<Booking>): ResourceRendererResult<Booking, BookingProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = BookingColumns.map(column => ({ label: i18n.t(`Booking::${column}`), value: column }))
  const selectedColumns: BookingColumnProperty[] = LocalStorage
    .get(LocalStorageKey.BOOKING_COLUMNS, DEFAULT_BOOKING_COLUMNS)
    .filter((column) => BookingColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...BookingColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (booking: Booking, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(booking.id)
    const actions: IActionListItem[] = [
      ...(booking.meeting_link ? [{ key: 'join', icon: 'video', content: i18n.t('Bookings::Join meeting') }] as IActionListItem[] : []),
      ...(booking.contact_id ? [
        { key: 'email', icon: 'envelope', content: i18n.t('Bookings::Email booker') },
        { key: 'contact', icon: 'user', content: i18n.t('Bookings::View contact') },
      ] as IActionListItem[] : []),
      { key: 'event_type', icon: 'calendar-clock', content: i18n.t('Bookings::Open event type') },
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${booking.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(booking) : undefined}
        maxWidth={ELLIPSED_COLUMNS.includes(column) ? '250px' : undefined}
        ellipse={ELLIPSED_COLUMNS.includes(column)}
        textAlign={BookingColumnConfigs[column].header().align}
      >
        {BookingColumnConfigs[column].render({ booking, ...options })}
      </ResourceTableRowData>
    ))

    if (onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${booking.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, booking)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={booking.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, booking.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getBookingTableRenderer
